import { useState, useEffect } from "react";
import axios from "axios";
import "./style.css";

export default function RequestRideModal({ driver, driverPost, user, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: locations, 2: details, 3: confirmation
  const [fare, setFare] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  
  const [rideData, setRideData] = useState({
    pickup: {
      address: "",
      lat: 0,
      lng: 0,
      instructions: "",
    },
    destination: {
      address: "",
      lat: 0,
      lng: 0,
      instructions: "",
    },
    distance: 0,
    estimatedDuration: 0,
    numberOfPassengers: 1,
    passengerNotes: "",
  });

  // Buscar saldo da wallet
  useEffect(() => {
    const fetchWalletBalance = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/wallet/balance`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setWalletBalance(data.balance || 0);
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
      }
    };
    fetchWalletBalance();
  }, [user.token]);

  // Calcular tarifa quando endereços mudam
  const calculateFare = async () => {
    if (!rideData.distance || !rideData.estimatedDuration) {
      alert("Por favor, preencha origem e destino");
      return;
    }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/uber/calculate-fare`,
        {
          driverPostId: driverPost._id,
          distance: rideData.distance,
          estimatedDuration: rideData.estimatedDuration,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setFare(data);
      setStep(3);
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao calcular tarifa");
    }
  };

  // Solicitar corrida
  const handleRequestRide = async () => {
    if (walletBalance < fare.totalFare) {
      alert("Saldo insuficiente na carteira. Por favor, adicione saldo.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/uber/ride/request`,
        {
          driverId: driver._id,
          driverPostId: driverPost._id,
          serviceType: driverPost.uberData.services[0], // serviço principal
          vehicleType: driverPost.uberData.vehicleType,
          ...rideData,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      onSuccess(data.ride);
      alert("Corrida solicitada com sucesso! O motorista foi notificado.");
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao solicitar corrida");
    }
    setLoading(false);
  };

  // Simulação de cálculo de distância (em produção, usar API do Google Maps)
  const estimateRoute = () => {
    if (!rideData.pickup.address || !rideData.destination.address) {
      alert("Preencha origem e destino");
      return;
    }

    // Simulação simples - em produção, usar API de mapas
    const estimatedDistance = Math.random() * 20 + 5; // 5-25 km
    const estimatedTime = Math.round(estimatedDistance * 3); // ~3 min por km

    setRideData({
      ...rideData,
      distance: parseFloat(estimatedDistance.toFixed(1)),
      estimatedDuration: estimatedTime,
    });

    setStep(2);
  };

  return (
    <div className="ride_modal_overlay" onClick={onClose}>
      <div className="ride_modal" onClick={(e) => e.stopPropagation()}>
        <button className="ride_modal_close" onClick={onClose}>✕</button>

        <div className="ride_modal_header">
          <h2>🚗 Solicitar Corrida</h2>
          <div className="ride_driver_info">
            <img src={driver.picture} alt={driver.first_name} />
            <div>
              <h3>{driver.first_name} {driver.last_name}</h3>
              <span>{driverPost.uberData.vehicleModel} • {driverPost.uberData.vehicleColor}</span>
            </div>
          </div>
        </div>

        <div className="ride_modal_body">
          {/* Step 1: Endereços */}
          {step === 1 && (
            <div className="ride_step">
              <h3>📍 Para onde você vai?</h3>
              
              <div className="ride_form_section">
                <label>Ponto de Partida</label>
                <input
                  type="text"
                  placeholder="Endereço de origem"
                  value={rideData.pickup.address}
                  onChange={(e) => setRideData({
                    ...rideData,
                    pickup: { ...rideData.pickup, address: e.target.value }
                  })}
                />
                <textarea
                  placeholder="Instruções adicionais (opcional)"
                  value={rideData.pickup.instructions}
                  onChange={(e) => setRideData({
                    ...rideData,
                    pickup: { ...rideData.pickup, instructions: e.target.value }
                  })}
                  rows={2}
                />
              </div>

              <div className="ride_form_section">
                <label>Destino</label>
                <input
                  type="text"
                  placeholder="Para onde você vai?"
                  value={rideData.destination.address}
                  onChange={(e) => setRideData({
                    ...rideData,
                    destination: { ...rideData.destination, address: e.target.value }
                  })}
                />
                <textarea
                  placeholder="Instruções adicionais (opcional)"
                  value={rideData.destination.instructions}
                  onChange={(e) => setRideData({
                    ...rideData,
                    destination: { ...rideData.destination, instructions: e.target.value }
                  })}
                  rows={2}
                />
              </div>

              <button className="ride_btn_primary" onClick={estimateRoute}>
                Calcular Rota
              </button>
            </div>
          )}

          {/* Step 2: Detalhes */}
          {step === 2 && (
            <div className="ride_step">
              <h3>ℹ️ Detalhes da Viagem</h3>

              <div className="ride_summary">
                <div className="ride_summary_item">
                  <span className="ride_summary_label">Distância</span>
                  <span className="ride_summary_value">{rideData.distance} km</span>
                </div>
                <div className="ride_summary_item">
                  <span className="ride_summary_label">Tempo estimado</span>
                  <span className="ride_summary_value">{rideData.estimatedDuration} min</span>
                </div>
              </div>

              <div className="ride_form_section">
                <label>Número de Passageiros</label>
                <select
                  value={rideData.numberOfPassengers}
                  onChange={(e) => setRideData({
                    ...rideData,
                    numberOfPassengers: parseInt(e.target.value)
                  })}
                >
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>{n} passageiro{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              <div className="ride_form_section">
                <label>Observações para o motorista (opcional)</label>
                <textarea
                  placeholder="Ex: Vou estar com uma mala grande"
                  value={rideData.passengerNotes}
                  onChange={(e) => setRideData({
                    ...rideData,
                    passengerNotes: e.target.value
                  })}
                  rows={2}
                />
              </div>

              <div className="ride_step_buttons">
                <button className="ride_btn_secondary" onClick={() => setStep(1)}>
                  Voltar
                </button>
                <button className="ride_btn_primary" onClick={calculateFare}>
                  Ver Tarifa
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmação */}
          {step === 3 && fare && (
            <div className="ride_step">
              <h3>💰 Confirmação e Pagamento</h3>

              <div className="ride_route_summary">
                <div className="ride_route_item">
                  <div className="ride_route_icon">📍</div>
                  <div>
                    <strong>Origem</strong>
                    <p>{rideData.pickup.address}</p>
                  </div>
                </div>
                <div className="ride_route_divider">⋮</div>
                <div className="ride_route_item">
                  <div className="ride_route_icon">🎯</div>
                  <div>
                    <strong>Destino</strong>
                    <p>{rideData.destination.address}</p>
                  </div>
                </div>
              </div>

              <div className="ride_fare_breakdown">
                <h4>Detalhamento da Tarifa</h4>
                <div className="fare_item">
                  <span>Tarifa base</span>
                  <span>R$ {fare.baseFare.toFixed(2)}</span>
                </div>
                <div className="fare_item">
                  <span>Distância ({rideData.distance} km)</span>
                  <span>R$ {fare.distanceFare.toFixed(2)}</span>
                </div>
                <div className="fare_item">
                  <span>Tempo ({rideData.estimatedDuration} min)</span>
                  <span>R$ {fare.timeFare.toFixed(2)}</span>
                </div>
                <div className="fare_item">
                  <span>Taxa de serviço (15%)</span>
                  <span>R$ {fare.serviceFee.toFixed(2)}</span>
                </div>
                <div className="fare_item fare_total">
                  <strong>Total</strong>
                  <strong>R$ {fare.totalFare.toFixed(2)}</strong>
                </div>
              </div>

              <div className="ride_wallet_info">
                <div className="wallet_balance">
                  <span>💳 Saldo da Carteira:</span>
                  <strong className={walletBalance >= fare.totalFare ? "sufficient" : "insufficient"}>
                    R$ {walletBalance.toFixed(2)}
                  </strong>
                </div>
                {walletBalance < fare.totalFare && (
                  <div className="wallet_warning">
                    ⚠️ Saldo insuficiente. Adicione R$ {(fare.totalFare - walletBalance).toFixed(2)} à sua carteira.
                  </div>
                )}
              </div>

              <div className="ride_step_buttons">
                <button className="ride_btn_secondary" onClick={() => setStep(2)}>
                  Voltar
                </button>
                <button 
                  className="ride_btn_primary" 
                  onClick={handleRequestRide}
                  disabled={loading || walletBalance < fare.totalFare}
                >
                  {loading ? "Solicitando..." : "Solicitar Corrida"}
                </button>
              </div>

              <div className="ride_payment_info">
                <small>
                  💡 O valor será reservado na sua carteira e transferido ao motorista apenas após a conclusão da corrida.
                </small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
